import { tr } from '@/lib/i18n-core';
import { useEffect, useState, type ReactNode } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { AvailabilitySlot, FollowUpOffer } from '@his/shared';
import { get, post, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import type { ServicePackage } from '../billing/PackagesTab';
import { dateOnly, dateTime, money, timeOnly, todayIso } from '@/lib/utils';
import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Field,
  Input,
  Select,
} from '@/components/ui/primitives';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { FormActions, FormSection } from '@/components/ui/form-screen';
import { useToast } from '@/components/ui/toast';
import { PatientPicker } from '../patients/PatientPicker';
import { ServicePicker, type ServiceChoice } from './calendar/ServicePicker';
import { payerChoiceComplete, payerRequest, SELF_PAY, type PayerChoice } from './payer-choice';

export type { PayerChoice } from './payer-choice';

export interface BookingDefaults {
  patientId?: string;
  doctorId?: string;
  type?: 'new' | 'follow_up';
  date?: string;
  slotAt?: string;
  service?: ServiceChoice | null;
}

export interface PayerFieldProps {
  patientId: string;
  date: string;
  at?: string | null;
  value: PayerChoice;
  onChange: (next: PayerChoice) => void;
}

export interface ProviderOption {
  id: string;
  name: string;
}

export function BookAppointmentForm({
  branchId,
  departmentId,
  defaults,
  providers,
  payerField,
  onBooked,
  onCancel,
  variant = 'dialog',
  onDirtyChange,
}: {
  branchId: string;
  departmentId?: string | null;
  defaults: BookingDefaults;
  providers?: ProviderOption[];
  payerField?: (props: PayerFieldProps) => ReactNode;
  onBooked: () => void;
  onCancel: () => void;
  variant?: 'dialog' | 'screen';
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const toast = useToast();
  const [patientId, setPatientId] = useState(defaults.patientId ?? '');
  const [doctorId, setDoctorId] = useState(defaults.doctorId ?? '');
  const [date, setDate] = useState(defaults.date ?? todayIso());
  const [slotAt, setSlotAt] = useState(defaults.slotAt ?? '');
  const [type, setType] = useState<'new' | 'follow_up'>(defaults.type ?? 'new');
  const [service, setService] = useState<ServiceChoice | null>(defaults.service ?? null);
  const [payer, setPayer] = useState<PayerChoice | null>(null);
  const [reason, setReason] = useState('');
  const [allowOverbook, setAllowOverbook] = useState(false);
  const [packageId, setPackageId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { can, hasModule } = useAuth();
  const maySellCheckUp = hasModule('billing') && can('invoice.create');
  const checkUps = useQuery({
    queryKey: ['packages', 'health_checkup', 'active'],
    queryFn: () => get<ServicePackage[]>(`/packages${qs({ kind: 'health_checkup', active: 1 })}`),
    enabled: maySellCheckUp,
  });

  const doctors = useQuery({
    queryKey: ['bookable-doctors'],
    queryFn: async () =>
      (await get<{ data: ProviderOption[] }>('/org/clinicians?kind=bookable&limit=200')).data,
    enabled: !providers,
  });
  const doctorOptions = providers ?? doctors.data;

  const durationMinutes = service?.durationMinutes;
  const availability = useQuery({
    queryKey: ['availability', doctorId, branchId, date, durationMinutes ?? 0],
    queryFn: () =>
      get<AvailabilitySlot[]>(
        `/schedules/availability${qs({ doctorId, branchId, date, durationMinutes })}`,
      ),
    enabled: Boolean(doctorId && date),
  });

  const entitlement = useQuery({
    queryKey: ['follow-up-entitlement', patientId, doctorId, branchId, slotAt],
    queryFn: () =>
      get<{ offer: FollowUpOffer | null }>(
        `/appointments/follow-up-entitlement${qs({ patientId, doctorId, branchId, slotAt })}`,
      ),
    enabled: Boolean(patientId && doctorId && slotAt),
  });
  const offer = entitlement.data?.offer ?? null;
  const offerUntil = offer ? dateOnly(offer.validUntil) : '';
  const followUpLine = !offer
    ? null
    : offer.kind === 'free'
      ? tr('opd', 'follow_up_free_until_x', { date: offerUntil })
      : offer.kind === 'discount'
        ? tr('opd', 'follow_up_discount_until_x', { percent: offer.discountPct, date: offerUntil })
        : tr('opd', 'follow_up_full_fee_until_x', { date: offerUntil });

  const chosen = availability.data?.find((slot) => slot.startsAt === slotAt);
  const pickedOffGrid = Boolean(slotAt && availability.data && !chosen);
  const overbookSeatsLeft = chosen
    ? Math.max(0, chosen.capacity + chosen.overbook - chosen.booked)
    : 0;
  const needsOverbook = Boolean(chosen && !chosen.available && overbookSeatsLeft > 0);
  const canOverbook = (slot: AvailabilitySlot) =>
    slot.overbook > 0 && slot.booked < slot.capacity + slot.overbook;

  const book = useMutation({
    mutationFn: () =>
      post<{ quotedUnitPriceMinor?: number | string | null }>('/appointments', {
        patientId,
        doctorId,
        branchId,
        departmentId: departmentId || undefined,
        slotAt,
        type: offer ? 'follow_up' : type,
        channel: 'desk',
        reason: reason || undefined,
        chargeItemId: service?.chargeItemId,
        durationMinutes: service?.durationMinutes,
        ...(payerField && payer ? payerRequest(payer) : {}),
        allowOverbook: needsOverbook && allowOverbook ? true : undefined,
        ...(packageId ? { packageId } : {}),
      }),
    onSuccess: (booked) => {
      const quoted = booked?.quotedUnitPriceMinor;
      toast.success(
        tr('opd', 'appointment_booked'),
        quoted !== null && quoted !== undefined
          ? tr('opd', 'fee_quoted_x', { value: money(Number(quoted)) })
          : undefined,
      );
      onBooked();
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const payerReady = !payerField || (payer !== null && payerChoiceComplete(payer));
  const ready =
    Boolean(patientId && doctorId && slotAt) &&
    payerReady &&
    !(needsOverbook && !allowOverbook) &&
    (!service || service.durationMinutes >= 5);

  const dirty =
    patientId !== (defaults.patientId ?? '') ||
    doctorId !== (defaults.doctorId ?? '') ||
    slotAt !== (defaults.slotAt ?? '') ||
    reason !== '' ||
    packageId !== '' ||
    service !== (defaults.service ?? null);
  useEffect(() => {
    onDirtyChange?.(dirty && !book.isSuccess);
  }, [dirty, book.isSuccess, onDirtyChange]);

  const submit = () => {
    setError(null);
    book.mutate();
  };

  const patientField = (
    <Field
      label={tr('opd', 'patient')}
      required
      className={variant === 'screen' ? 'sm:col-span-2' : undefined}
    >
      <PatientPicker
        value={patientId}
        onChange={(next) => {
          setPatientId(next);
          setPayer(null);
        }}
      />
    </Field>
  );

  const doctorField = (
    <Field label={tr('opd', 'doctor')} required>
      <Select
        value={doctorId}
        onChange={(e) => {
          setDoctorId(e.target.value);
          setSlotAt('');
        }}
      >
        <option value="">{tr('opd', 'select_a_doctor')}</option>
        {doctorOptions?.map((doctor) => (
          <option key={doctor.id} value={doctor.id}>
            {doctor.name}
          </option>
        ))}
      </Select>
    </Field>
  );

  const dateField = (
    <Field label={tr('opd', 'date')} required>
      <DatePicker
        value={date}
        onChange={(value) => {
          setDate(value);
          setSlotAt('');
        }}
      />
    </Field>
  );

  const serviceField = (
    <ServicePicker
      departmentId={departmentId}
      value={service}
      onChange={(next) => {
        setService(next);
        if (next?.durationMinutes !== service?.durationMinutes) setSlotAt('');
      }}
    />
  );

  const slotField = (
    <Field
      label={tr('opd', 'slot')}
      required
      className={variant === 'screen' ? 'sm:col-span-2' : undefined}
      hint={
        doctorId && availability.data?.length === 0 ? tr('opd', 'no_clinic_on_this_day') : undefined
      }
    >
      {availability.isFetching ? (
        <p className="text-sm text-muted-foreground">{tr('opd', 'checking_availability')}</p>
      ) : (
        <div className="grid max-h-48 grid-cols-3 gap-1.5 overflow-y-auto rounded-md border p-2 sm:grid-cols-4">
          {pickedOffGrid && (
            <button
              type="button"
              className="col-span-full rounded bg-primary px-2 py-1 text-xs tabular text-primary-foreground focus-ring"
              onClick={() => setAllowOverbook(false)}
            >
              {tr('opd', 'chosen_on_the_calendar_x', { time: timeOnly(slotAt) })}
            </button>
          )}
          {availability.data?.map((slot) => (
            <button
              key={slot.startsAt}
              type="button"
              disabled={!slot.available && !canOverbook(slot)}
              title={
                !slot.available && canOverbook(slot)
                  ? tr('opd', 'full_overbook_seat_available')
                  : undefined
              }
              onClick={() => {
                setSlotAt(slot.startsAt);
                setAllowOverbook(false);
              }}
              className={`rounded px-2 py-1 text-xs tabular transition-colors focus-ring ${
                slotAt === slot.startsAt
                  ? 'bg-primary text-primary-foreground'
                  : slot.available
                    ? 'bg-muted hover:bg-accent'
                    : canOverbook(slot)
                      ? 'bg-amber-100 text-amber-900 hover:bg-amber-200 dark:bg-amber-900/40 dark:text-amber-100'
                      : 'cursor-not-allowed bg-muted/40 text-muted-foreground line-through'
              }`}
            >
              {timeOnly(slot.startsAt)}
              {!slot.available && canOverbook(slot) ? ' +' : ''}
            </button>
          ))}
          {availability.data?.length === 0 && (
            <p className="col-span-full py-4 text-center text-sm text-muted-foreground">
              {tr('opd', 'this_doctor_has_no_clinic_on_the_selected_day')}{' '}
              <span className="font-medium">{tr('opd', 'today_clinic_hours')}</span>.
            </p>
          )}
        </div>
      )}
    </Field>
  );

  const typeField = (
    <Field label={tr('opd', 'visit_type')}>
      <ChoiceButtons
        value={offer ? 'follow_up' : type}
        disabled={Boolean(offer)}
        onChange={setType}
        options={[
          { value: 'new', label: tr('opd', 'new') },
          { value: 'follow_up', label: tr('opd', 'follow_up') },
        ]}
      />
    </Field>
  );

  const reasonField = (
    <Field label={tr('opd', 'reason')}>
      <Input
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder={tr('opd', 'presenting_complaint')}
      />
    </Field>
  );

  const checkUpField =
    maySellCheckUp && (checkUps.data?.length ?? 0) > 0 ? (
      <Field label={tr('billing', 'health_checkup_package')} hint={tr('billing', 'package_kind_hint_health_checkup')}>
        <Select value={packageId} onChange={(e) => setPackageId(e.target.value)}>
          <option value="">{tr('billing', 'no_package')}</option>
          {checkUps.data?.map((pkg) => (
            <option key={pkg.id} value={pkg.id}>
              {pkg.name} · {money(pkg.priceMinor)}
            </option>
          ))}
        </Select>
      </Field>
    ) : null;

  const payerBlock =
    payerField && patientId
      ? payerField({
          patientId,
          date,
          at: slotAt || null,
          value: payer ?? SELF_PAY,
          onChange: setPayer,
        })
      : null;

  const notices = (
    <>
      {slotAt && !needsOverbook && (
        <Alert variant="success">
          {tr('opd', 'booking_for')} {dateTime(slotAt)}
        </Alert>
      )}
      {followUpLine && <Alert>{followUpLine}</Alert>}
      {needsOverbook && (
        <Alert variant="warning">
          <Checkbox
            checked={allowOverbook}
            onCheckedChange={setAllowOverbook}
            label={
              <>
                {dateTime(slotAt)} {tr('opd', 'is_full_book_into_an_overbook_seat')}{' '}
                {overbookSeatsLeft} {tr('opd', 'left_the_doctor_will_see_one_extra_patient_in')}
              </>
            }
          />
        </Alert>
      )}
      {error && <Alert variant="destructive">{error}</Alert>}
    </>
  );

  const submitLabel = needsOverbook ? tr('opd', 'book_overbook_seat') : tr('opd', 'book');

  if (variant === 'screen') {
    return (
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          if (ready) submit();
        }}
      >
        <div className="space-y-5">
          <FormSection title={tr('opd', 'patient')} columns={1}>
            {patientField}
          </FormSection>
          <FormSection
            title={tr('opd', 'booking_section_when')}
            description={tr('opd', 'pick_the_patient_the_doctor_and_a_free_slot')}
          >
            {doctorField}
            {dateField}
            <div className="sm:col-span-2">{serviceField}</div>
            {slotField}
          </FormSection>
          <FormSection title={tr('opd', 'booking_section_visit')}>
            {typeField}
            {reasonField}
            {checkUpField && <div className="sm:col-span-2">{checkUpField}</div>}
            {payerBlock && <div className="sm:col-span-2">{payerBlock}</div>}
          </FormSection>
          {notices}
        </div>
        <FormActions
          submitLabel={submitLabel}
          loading={book.isPending}
          incomplete={!ready}
          onCancel={onCancel}
        />
      </form>
    );
  }

  return (
    <div className="space-y-4">
      {patientField}
      <div className="grid gap-4 sm:grid-cols-2">
        {doctorField}
        {dateField}
      </div>
      {serviceField}
      {slotField}
      <div className="grid gap-4 sm:grid-cols-2">
        {typeField}
        {reasonField}
      </div>
      {checkUpField}
      {payerBlock}
      {notices}
      <div className="flex justify-end gap-2 border-t pt-4">
        <Button variant="ghost" onClick={onCancel}>
          {tr('opd', 'cancel')}
        </Button>
        <Button onClick={submit} loading={book.isPending} disabled={!ready}>
          {submitLabel}
        </Button>
      </div>
    </div>
  );
}
